import {
  Box,
  Collapse,
  Divider,
  FieldLabel,
  Flex,
  Form,
  MonetaryInputField,
  NumericFormatField,
  SubmitButton,
  Text,
} from '@/components';
import { isBefore2025 } from '@/features/project/_utils/project';
import type { executionProgressType } from '@/features/project/projects/_models/execution-progress';
import { SubProjectUpdateFundBusinessFormSchema } from '@/features/project/projects/_models/sub-project';
import { useForm } from '@/hooks';
import { useLocaledAmount } from '@/providers';
import { formatValueByUnit } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { type FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type FormValues = z.infer<typeof SubProjectUpdateFundBusinessFormSchema>;

type Props = {
  fiscalYear: number;
  initialValues?: Partial<FormValues>;
  onSubmit: (values: FormValues) => void;
  defaultActiveKey?: z.infer<typeof executionProgressType>;
};

export const UpdateSubProjectIncomeAndExpenditureAndFundBusinessForm: FC<
  Props
> = ({ fiscalYear, initialValues, onSubmit, defaultActiveKey }) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();

  const {
    control,
    watch,
    setValue,
    reset,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
  } = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(SubProjectUpdateFundBusinessFormSchema),
  });

  const sumOfTheEndOfTheFundBalance =
    (watch('beginningOfTheFundBalance') ?? 0) +
    (watch('incomeAmount') ?? 0) -
    (watch('businessExpense') ?? 0) -
    (watch('amountReturnedToTheNationalTreasury') ?? 0);

  const previousFiscalYear = fiscalYear - 1;

  const before2025 = isBefore2025({ fiscalYear });

  const autoCalculateNationalEquivalentOfIncomeAmount = useCallback(() => {
    if (before2025) {
      return;
    }
    const targets = getValues([
      'grantedAmountByTheCountryFromInitial',
      'grantedAmountByTheCountryFromSupplementary',
      'grantedAmountByTheCountryFromReserve',
      'otherIncomeAmount',
    ]);

    const allNull = targets.every((target) => typeof target !== 'number');
    const sum = targets.reduce<number>((acc, cur) => acc + (cur ?? 0), 0);

    setValue('nationalEquivalentOfIncomeAmount', allNull ? null : sum);
  }, [before2025, getValues, setValue]);

  useEffect(() => {
    reset(initialValues);
  }, [initialValues, reset]);

  const decidedFormFields = [
    {
      label: t('fund-business-subsidy'),
      countName: 'decidedNumberOfSubsidy',
      amountName: 'decidedAmountOfSubsidy',
    },
    {
      label: t('investment'),
      countName: 'decidedNumberOfInvestment',
      amountName: 'decidedAmountOfInvestment',
    },
    {
      label: t('loan'),
      countName: 'decidedNumberOfLoan',
      amountName: 'decidedAmountOfLoan',
    },
    {
      label: t('debt-guarantee'),
      countName: 'decidedNumberOfDebtGuarantee',
      amountName: 'decidedAmountOfDebtGuarantee',
    },
    {
      label: t('fund-business-for-local-government-others'),
      countName: 'decidedNumberOfOthers',
      amountName: 'decidedAmountOfOthers',
    },
  ] as const;

  const balancedFormFields = [
    {
      label: t('investment'),
      countName: 'balanceNumberOfInvestment',
      amountName: 'balanceAmountOfInvestment',
    },
    {
      label: t('loan'),
      countName: 'balanceNumberOfLoan',
      amountName: 'balanceAmountOfLoan',
    },
    {
      label: t('debt-guarantee'),
      countName: 'balanceNumberOfDebtGuarantee',
      amountName: 'balanceAmountOfDebtGuarantee',
    },
  ] as const;

  const collapseItems = [
    {
      key: 'income-and-expenditure',
      label: t('edit-income-and-expenditure'),
      children: (
        <Flex vertical gap={16}>
          <Text variant="h5" text={previousFiscalYear - 1} />
          <Form.Item
            style={{ marginTop: 16 }}
            label={<FieldLabel text={t('fund-balance')} />}
          >
            <Flex vertical gap={8}>
              <Flex align="center" gap={8}>
                <Box flex={1}>
                  <Text text={t('sum-amount')} weight="regular" />
                </Box>
                <Box flex={5}>
                  <MonetaryInputField
                    control={control}
                    name="beginningOfTheFundBalance"
                    errors={errors}
                    unit={unit}
                    marginBottom={0}
                  />
                </Box>
              </Flex>
              <Flex align="center" gap={8}>
                <Box flex={1}>
                  <Text
                    text={t('amount-equivalent-to-national-expense')}
                    weight="regular"
                  />
                </Box>
                <Box flex={5}>
                  <MonetaryInputField
                    name="nationalEquivalentOfBeginningOfTheFundBalance"
                    control={control}
                    errors={errors}
                    unit={unit}
                    marginBottom={0}
                  />
                </Box>
              </Flex>
            </Flex>
          </Form.Item>
          <Divider size="md" />
          <Text variant="h5" text={previousFiscalYear} />
          <Box marginTop={16}>
            <Form.Item label={<FieldLabel text={t('income')} />}>
              <Flex vertical gap={8}>
                <Flex align="center" gap={8}>
                  <Box flex={1}>
                    <Text text={t('sum-amount')} weight="regular" />
                  </Box>
                  <Box flex={5}>
                    <MonetaryInputField
                      name="incomeAmount"
                      control={control}
                      errors={errors}
                      unit={unit}
                      marginBottom={0}
                    />
                  </Box>
                </Flex>
                <Flex align="center" gap={8}>
                  <Box flex={1}>
                    <Text
                      text={t('amount-equivalent-to-national-expense')}
                      weight="regular"
                    />
                  </Box>
                  <Box flex={5}>
                    <MonetaryInputField
                      name="nationalEquivalentOfIncomeAmount"
                      control={control}
                      errors={errors}
                      unit={unit}
                      marginBottom={0}
                      disabled={!before2025}
                    />
                  </Box>
                </Flex>
              </Flex>
            </Form.Item>
          </Box>
          <Form.Item
            label={
              <FieldLabel text={t('amount-of-funding-from-the-government')} />
            }
          >
            <Flex vertical gap={8}>
              <Flex align="center" gap={8}>
                <Box flex={1}>
                  <Text text={t('initial')} weight="regular" />
                </Box>
                <Box flex={5}>
                  <MonetaryInputField
                    name="grantedAmountByTheCountryFromInitial"
                    control={control}
                    errors={errors}
                    unit={unit}
                    marginBottom={0}
                    shouldCoexistOnChange
                    onChange={autoCalculateNationalEquivalentOfIncomeAmount}
                  />
                </Box>
              </Flex>
              <Flex align="center" gap={8}>
                <Box flex={1}>
                  <Text text={t('correction')} weight="regular" />
                </Box>
                <Box flex={5}>
                  <MonetaryInputField
                    name="grantedAmountByTheCountryFromSupplementary"
                    control={control}
                    errors={errors}
                    unit={unit}
                    marginBottom={0}
                    shouldCoexistOnChange
                    onChange={autoCalculateNationalEquivalentOfIncomeAmount}
                  />
                </Box>
              </Flex>
              <Flex align="center" gap={8}>
                <Box flex={1}>
                  <Text text={t('reserve-budget')} weight="regular" />
                </Box>
                <Box flex={5}>
                  <MonetaryInputField
                    name="grantedAmountByTheCountryFromReserve"
                    control={control}
                    errors={errors}
                    unit={unit}
                    marginBottom={0}
                    shouldCoexistOnChange
                    onChange={autoCalculateNationalEquivalentOfIncomeAmount}
                  />
                </Box>
              </Flex>
            </Flex>
          </Form.Item>
          <MonetaryInputField
            hint={t('hint-other-income-amount')}
            label={t('others')}
            name="otherIncomeAmount"
            control={control}
            errors={errors}
            unit={unit}
            shouldCoexistOnChange
            onChange={autoCalculateNationalEquivalentOfIncomeAmount}
          />
          <MonetaryInputField
            label={t('expense')}
            name="businessExpense"
            control={control}
            errors={errors}
            unit={unit}
          />
          <MonetaryInputField
            label={t('amount-of-national-treasury-return')}
            name="amountReturnedToTheNationalTreasury"
            control={control}
            errors={errors}
            unit={unit}
          />
          <Divider size="md" />
          <Text
            variant="h5"
            text={t('end-of-year-with-year', {
              year: previousFiscalYear,
            })}
          />
          <Box marginTop={16}>
            <Form.Item label={t('fund-balance')}>
              <Flex vertical gap={8}>
                <Flex align="center" gap={8}>
                  <Box flex={1}>
                    <Text text={t('sum-amount')} weight="regular" />
                  </Box>
                  <Box flex={5}>
                    <Text
                      text={formatValueByUnit(sumOfTheEndOfTheFundBalance, {
                        operation: 'round',
                        unit,
                        showUnitLabel: true,
                      })}
                    />
                  </Box>
                </Flex>
                <Flex align="center" gap={8}>
                  <Box flex={1}>
                    <Text
                      text={t('amount-equivalent-to-national-expense')}
                      weight="regular"
                    />
                  </Box>
                  <Box flex={5}>
                    <MonetaryInputField
                      name="nationalEquivalentOfTheEndOfTheFundBalance"
                      control={control}
                      errors={errors}
                      unit={unit}
                      marginBottom={0}
                    />
                  </Box>
                </Flex>
              </Flex>
            </Form.Item>
          </Box>
        </Flex>
      ),
    },
    {
      key: 'fund-business',
      label: t('edit-fund-business'),
      children: (
        <Flex vertical gap={16}>
          <Text variant="h5" text={previousFiscalYear} />
          <Form.Item
            label={
              <FieldLabel text={t('decision-on-project-implementation')} />
            }
          >
            <Flex vertical gap={8}>
              {decidedFormFields.map((field) => (
                <Flex key={field.countName} align="center">
                  <Box flex={2}>
                    <Text text={field.label} weight="regular" />
                  </Box>
                  <Box flex={2} marginLeft={8} marginRight={16}>
                    <NumericFormatField
                      control={control}
                      name={field.countName}
                      errors={errors}
                      addonAfter={t('number-unit')}
                      marginBottom={0}
                      allowNegative={false}
                    />
                  </Box>
                  <Box flex={5}>
                    <MonetaryInputField
                      control={control}
                      name={field.amountName}
                      errors={errors}
                      unit={unit}
                      marginBottom={0}
                    />
                  </Box>
                </Flex>
              ))}
            </Flex>
          </Form.Item>
          <Form.Item
            label={<FieldLabel text={t('fund-business-loan-balance-title')} />}
          >
            <Flex vertical gap={8}>
              {balancedFormFields.map((field) => (
                <Flex key={field.countName} align="center">
                  <Box flex={2}>
                    <Text text={field.label} weight="regular" />
                  </Box>
                  <Box flex={2} marginLeft={8} marginRight={16}>
                    <NumericFormatField
                      control={control}
                      errors={errors}
                      name={field.countName}
                      addonAfter={t('number-unit')}
                      marginBottom={0}
                      allowNegative={false}
                    />
                  </Box>
                  <Box flex={5}>
                    <MonetaryInputField
                      control={control}
                      name={field.amountName}
                      errors={errors}
                      unit={unit}
                      marginBottom={0}
                    />
                  </Box>
                </Flex>
              ))}
            </Flex>
          </Form.Item>
        </Flex>
      ),
    },
  ];

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <Collapse
        style={{
          backgroundColor: 'transparent',
        }}
        bordered={false}
        defaultActiveKey={defaultActiveKey}
        items={collapseItems}
        accordion={false}
      />
      <Form.Item style={{ marginTop: 24 }}>
        <SubmitButton text={t('save')} loading={isSubmitting} />
      </Form.Item>
    </Form>
  );
};
